// Rules -> Routing — the routing rules (owner, 2026-09-15). Since D-336
// (owner, 2026-10-09) each rule is a condition stack and the approvals a
// matching invoice requires: "our table should have columns Rule, Controller,
// Manager, Director, Oversight, Status. Rule sentences should be structured as
// "When... , require the following approvals:". Then, include a checkmark in
// the cells where approval is required. In the Add/Edit Rule pop-up, list each
// of the approval steps with toggles next to each one." ONE list: the
// Controller Approval / Oversight Approval switcher of D-304 is gone.
//
// ITS OWN FILE rather than a fifth manager inside `RulesScreen.tsx`. That file
// is already 3,100 lines carrying four managers, and this one brings a
// condition builder with it; the split is the same call `companyAliases.ts`
// made on the route side, for the same reason.
//
// EVERY CONVENTION OF THE RULES SCREEN APPLIES UNCHANGED, and each is a real
// requirement rather than a resemblance:
//
//   * Rule, the four approval columns and Status, the rule a full sentence
//     built by `lib/oversightRuleSentence.ts` — one builder, so the row here
//     and the routing rows in an invoice's History cannot describe one rule
//     differently.
//   * The ROW is the edit control (`rowActivationProps`), not a pencil.
//   * Active / Inactive is a dot and a word (`StatusDot`), never a pill.
//   * One Filter button in the header cluster, carrying its count, with
//     Status: Active counting as one.
//   * The shared row density, and sortable Rule and Status headers.
//   * A checkbox per row, Select All in the header's corner and the Mark
//     Active / Mark Inactive strip (QA wave B, 2026-09-25, B-09; D-173).
//   * ONE TABLE: the list is the same `RuleTable` every other tab draws (code
//     review G-10), so its headers, its row density and its dimmed Inactive
//     row cannot drift from theirs.
//   * The row is named "Edit: <sentence>", like every other rule row, and an
//     Inactive row is dimmed.
//   * The editor is titled Add Rule / Edit Rule, saves with Save, and shows
//     Status on EDIT ONLY — a new rule is Active.
//   * Every placeholder is an EXAMPLE, never a description of the control.
//
// THE EMPTY STATE NAMES THE CONSEQUENCE. With no rules, every invoice takes the
// default route, Controller Approval and then Director Approval (D-350).
//
// RULES AN OVERRIDE CREATED (owner, 2026-10-12 — D-350: "rules set via
// Override appearing inside Rules > Routing with a way to filter for them, so
// they can be set to Inactive"; amended the same day). The Filter dialog's
// "Source" starts on Rules, so those rules are hidden until Memory is ticked
// (they were "Overrides" until D-367), and Reset returns it there (renamed from "Written By",
// owner, 2026-10-13 — D-352). The editor says who
// created a rule and from where; that never changes, whoever edits the rule
// after ("The source is the source"). Every rule is editable here, and both
// kinds route alike.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Option,
  Spinner,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { FieldInput } from '../../components/FieldControls'
import { AppDialog } from '../../components/AppDialog'
import { useDialogFormStyles } from '../../components/dialogForm'
import { AddRegular, CheckmarkRegular, DeleteRegular } from '@fluentui/react-icons'
import {
  APPROVAL_REQUIREMENT_LABELS,
  APPROVAL_REQUIREMENTS,
  DEFAULT_REQUIRED_APPROVALS,
  REQUIRED_APPROVAL_FIELD,
  ROUTING_RULE_SOURCE,
  type ApprovalRequirement,
} from '@ap/shared'
import type {
  OversightRule,
  OversightRuleField,
  OversightRuleScope,
  RequiredApprovals,
} from '../../api/types'
import { StatusToggle } from '../../components/StatusToggle'
import { ListFilters } from '../../components/ListFilters'
import { MultiSelectField, type MultiSelectOption } from '../../components/MultiSelectField'
import { FrozenWhileClosed } from '../../components/FrozenWhileClosed'
import { ScreenActions } from '../../components/ScreenActions'
import { RuleTable, type RuleTableColumn } from '../../components/RuleTable'
import { errorMessage } from '../../api/errors'
import { useDialogError } from '../../lib/hooks'
import { filterMoneyInput, formatCurrency, NOTHING_CHANGED, savedToast, vendorOptionSubtext } from '../../lib/format'
import {
  createOversightRule,
  loadAllCodes,
  loadCompaniesForManagement,
  loadDepartments,
  loadAccounts,
  loadOversightRules,
  searchVendors,
  updateOversightRule,
} from '../../lib/data'
import { ROUTING_RULE_CLOSING, renderOversightRuleSentence } from '../../lib/oversightRuleSentence'
import { ruleCreatedLine } from '../../lib/routeOverride'
import {
  MAX_OVERSIGHT_CONDITIONS_PER_RULE,
  OVERSIGHT_FIELD_LABEL,
  OVERSIGHT_FIELD_TYPE,
  OVERSIGHT_OPERATOR_LABEL,
  OVERSIGHT_RULE_SCOPES,
  OVERSIGHT_SCOPE_CLAUSE,
  type OversightRuleConditionDraft,
  blankCondition,
  conditionIsComplete,
  sourceSystemOptions,
  fieldsForScope,
  sameConditions,
  operatorsFor,
  toggleRequiredApproval,
} from '../../lib/oversightRules'
import { DEFAULT_STATUS_FILTER, type StatusFilter, matchesStatus } from '../../lib/statusFilter'
import { DEFAULT_RULE_SOURCE_FILTER, RULE_SOURCE_OPTIONS, matchesRuleSource } from '../../lib/ruleSource'
import { useToast } from '../../components/useToast'
import { ApprovalPathPicker } from '../../components/Timeline'

const useStyles = makeStyles({
  // The condition stack. Each row is one clause of the sentence, read top to
  // bottom, with the connective words as text between them rather than as
  // labels — the labels-are-clauses arrangement the other four tabs use cannot
  // work where the rows repeat.
  // WIDER THAN A DEFAULT DIALOG. Four controls and a remove button do not fit
  // Fluent’s stock surface, and at that width the value field is clipped behind
  // a horizontal scrollbar — which hides the half of the row a person is
  // actually filling in. Capped at the viewport so a narrow window still fits.
  surface: { maxWidth: 'min(860px, calc(100vw - 48px))', width: 'min(860px, calc(100vw - 48px))' },
  builder: { display: 'flex', flexDirection: 'column', gap: '4px' },
  conditionRow: {
    display: 'grid',
    // FOUR EQUAL COLUMNS, then the remove button (owner, 2026-10-07: "The
    // fields currently overlap each other, when they should be evenly
    // spaced"). `minmax(0, 1fr)` lets each track be exactly a quarter of the
    // row; `field` below lets each control fill its track and no more.
    //
    // WHY THEY OVERLAPPED: Fluent's `Dropdown` root carries `min-width: 250px`
    // (`app/README.md`, Fluent gotchas). The tracks were `minmax(120px, 0.8fr)`
    // and the like, whose fixed 120px floor means the grid never sizes a track
    // by its item, so at the dialog's width each 250px Dropdown sat in a track
    // of about 140-160px and spilled across the gap into the next column. The
    // wider gap (14px, 2026-09-21) only moved the overlap.
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr)) auto',
    // 14px, not 8 (owner, 2026-09-21): four bordered controls abutting each
    // other read as one segmented strip, because adjacent borders look like
    // shared ones.
    gap: '14px',
    alignItems: 'end',
    minWidth: 0,
    // Vertical breathing room between stacked conditions, for the same reason.
    paddingTop: '2px',
    paddingBottom: '2px',
  },
  // Each control fills its own track and never forces it wider: Dropdown's
  // 250px minimum (and any control's intrinsic width) cleared.
  field: { minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
  connective: { color: tokens.colorNeutralForeground2, paddingLeft: '4px' },
  addRow: { marginTop: '4px', alignSelf: 'flex-start' },
  error: { color: tokens.colorPaletteRedForeground1, display: 'block', marginTop: '8px' },
  // A checkmark under a step the rule requires.
  check: { fontSize: '16px', color: tokens.colorBrandForeground1, verticalAlign: 'middle' },
  // Read by a screen reader, not seen: the checkmark's words, and the empty
  // cell's (Fluent's visually-hidden recipe).
  hidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  // Who created the rule and from where (D-350 amended), above its sentence.
  origin: { color: tokens.colorNeutralForeground2, display: 'block', marginBottom: '12px' },
  // Full width, like the Status field on every other Rules editor (owner,
  // 2026-09-21). It was capped at 200px, which left it looking like a stray
  // control rather than the last field of the form.
})

type EditTarget = OversightRule | 'new' | null

const ruleId = (rule: OversightRule) => rule.id
const ruleIsActive = (rule: OversightRule) => rule.is_active
const writeRuleActive = (rule: OversightRule, active: boolean) =>
  updateOversightRule(rule.id, { is_active: active, row_version: rule.row_version })

// ---------------------------------------------------------------------------
// The manager
// ---------------------------------------------------------------------------

/** The list's own words. */
const LIST_NAME = 'Routing rules'
const EMPTY = 'No rules yet — every invoice requires Controller Approval and Director Approval.'

// The Filter dialog's "Source" (D-350, renamed D-352): where the rule was
// created. A rule an Override created is MEMORY since D-367, the word every
// Rules tab now uses for what the system wrote down on its own; it opens on
// Rules alone, as it did. A multi-select since 2026-10-14 (owner): neither or
// both ticked shows every rule. `lib/ruleSource.ts` holds the vocabulary.

/** One approval column: its step's name in the header, a checkmark where required. */
function RequiredCell({ required, step }: { required: boolean; step: string }) {
  const s = useStyles()
  return required ? (
    <span>
      <CheckmarkRegular className={s.check} aria-hidden />
      <span className={s.hidden}>{`${step} required`}</span>
    </span>
  ) : (
    <span className={s.hidden}>{`${step} not required`}</span>
  )
}

/** The header word: the step's name without "Approval" ("Controller"). */
const COLUMN_HEADER: Readonly<Record<ApprovalRequirement, string>> = {
  controller: 'Controller',
  manager: 'Manager',
  director: 'Director',
  oversight: 'Oversight',
}

const APPROVAL_COLUMNS: readonly RuleTableColumn<OversightRule>[] = APPROVAL_REQUIREMENTS.map((key) => ({
  key,
  header: COLUMN_HEADER[key],
  width: 84,
  cell: (rule: OversightRule) => (
    <RequiredCell required={rule[REQUIRED_APPROVAL_FIELD[key]]} step={APPROVAL_REQUIREMENT_LABELS[key]} />
  ),
  // Required sorts before not required on ascending.
  sortKey: (rule: OversightRule) => (rule[REQUIRED_APPROVAL_FIELD[key]] ? '0' : '1'),
}))

export function OversightRulesManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<OversightRule[] | null>(null)
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [source, setSource] = useState<string[]>(DEFAULT_RULE_SOURCE_FILTER)
  const [term, setTerm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<EditTarget>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadOversightRules())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const sentence = useCallback((rule: OversightRule) => renderOversightRuleSentence(rule), [])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (all ?? [])
      .filter((rule) => matchesStatus(status, rule.is_active))
      .filter((rule) => matchesRuleSource(source, rule.source === ROUTING_RULE_SOURCE.override))
      // Search matches the SENTENCE here, unlike the Codes tab, because a rule's
      // stored fields are enum keys (`line_department`, `is_at_least`) that
      // nobody types. The words on screen are the only searchable thing.
      .filter((rule) => !q || sentence(rule).toLowerCase().includes(q))
  }, [all, status, source, term, sentence])

  if (all === null) return <Spinner label={`Loading ${LIST_NAME.toLowerCase()}…`} />

  return (
    <div>
      {error && <Body1 className={s.error}>{error}</Body1>}
      <ScreenActions>
        <Tooltip content="Add Rule" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search rules…"
          ariaLabel={LIST_NAME}
          selects={[
            {
              multiple: true,
              label: 'Source',
              values: source,
              onChange: setSource,
              options: RULE_SOURCE_OPTIONS,
              defaultValues: DEFAULT_RULE_SOURCE_FILTER,
            },
          ]}
        />
      </ScreenActions>
      <RuleTable
        rows={visible}
        emptyText={(all ?? []).length === 0 ? EMPTY : 'No rules match.'}
        ariaLabel={LIST_NAME}
        selectAllLabel={LIST_NAME}
        keyOf={ruleId}
        isActive={ruleIsActive}
        sentence={sentence}
        columns={APPROVAL_COLUMNS}
        // Status alone, like every other tab's strip: one PATCH per rule, each
        // leaving its own audit row (D-350 amended). A refusal stops the run
        // with the count that landed (`useBulkMark`).
        write={writeRuleActive}
        noun="rule"
        onEdit={setEdit}
        onMarked={(m: string) => {
          toast(m)
          void load()
        }}
        onError={setError}
      />
      <OversightRuleDialog
        row={edit}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

function OversightRuleDialog({
  row,
  onClose,
  onSaved,
}: {
  row: EditTarget
  onClose: () => void
  /** The toast, from `savedToast` — the dialog knows whether Status moved. */
  onSaved: (message: string) => void
}) {
  const s = useStyles()
  const f = useDialogFormStyles()
  // THIS DIALOG'S OWN REFUSAL (QA wave A, 2026-09-22, A-01). It used to go to
  // the manager's screen-level line, which renders behind the modal.
  const [saveError, setSaveError] = useDialogError(row)
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [conditions, setConditions] = useState<OversightRuleConditionDraft[]>([])
  const [isActive, setIsActive] = useState(true)
  // A new rule starts on the default route, Controller Approval and Director
  // Approval (D-350: "Controller Approval is the new default starting point,
  // and that should be reflected across the board"); Manager Approval and
  // Director Approval from D-336 until then.
  const [approvals, setApprovals] = useState<RequiredApprovals>({ ...DEFAULT_REQUIRED_APPROVALS })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setConditions([blankCondition(0)])
      setIsActive(true)
      setApprovals({ ...DEFAULT_REQUIRED_APPROVALS })
      return
    }
    setConditions(
      [...row.conditions]
        .sort((a, b) => a.position - b.position)
        .map(({ value_labels: _labels, ...rest }) => rest),
    )
    setIsActive(row.is_active)
    setApprovals({
      requires_controller: row.requires_controller,
      requires_manager: row.requires_manager,
      requires_director: row.requires_director,
      requires_oversight: row.requires_oversight,
    })
  }, [row])

  const ready = conditions.length > 0 && conditions.every(conditionIsComplete)
  // The names the saved rule's ids resolved to on read, so a vendor no search
  // has listed yet still reads as its name in the editor (D-350).
  const knownLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    for (const condition of existing?.conditions ?? []) {
      condition.value_ids?.forEach((id, i) => {
        const label = condition.value_labels?.[i]
        if (label) labels[id] = label
      })
    }
    return labels
  }, [existing])

  function update(index: number, next: OversightRuleConditionDraft) {
    setConditions((previous) => previous.map((c, i) => (i === index ? next : c)))
  }

  function remove(index: number) {
    setConditions((previous) =>
      previous.filter((_, i) => i !== index).map((c, i) => ({ ...c, position: i })),
    )
  }

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      const payload = conditions.map((condition, index) => ({ ...condition, position: index }))
      // A Save that changed nothing writes nothing (D-170).
      const sameApprovals =
        existing !== null && APPROVAL_REQUIREMENTS.every((key) => existing[REQUIRED_APPROVAL_FIELD[key]] === approvals[REQUIRED_APPROVAL_FIELD[key]])
      if (existing && isActive === existing.is_active && sameApprovals && sameConditions(existing.conditions, payload)) {
        onSaved(NOTHING_CHANGED)
        return
      }
      if (existing) {
        await updateOversightRule(existing.id, {
          ...approvals,
          conditions: payload,
          is_active: isActive,
          row_version: existing.row_version,
        })
      } else {
        await createOversightRule({ ...approvals, conditions: payload })
      }
      onSaved(savedToast('rule', { wasNew: isNew, activeBefore: existing?.is_active, activeAfter: isActive }))
    } catch (e) {
      setSaveError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={row !== null} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={s.surface}>
        <FrozenWhileClosed open={row !== null}><DialogBody>
          <DialogTitle>{isNew ? 'Add Rule' : 'Edit Rule'}</DialogTitle>
          {/* The buttons 24px below the sentence (`dialogForm.ts`, D-349). The
              sentence itself is not a stack of labelled fields — its words
              sit between the rows — and keeps its own spacing. */}
          <DialogContent className={f.beforeActions}>
            {saveError && <Body1 className={s.error}>{saveError}</Body1>}
            {existing && <Body1 className={s.origin}>{ruleCreatedLine(existing)}</Body1>}
            {/* The stack IS the sentence, read top to bottom: "When", a clause
                per row joined by "and", then the closing clause. There is no
                grey preview box above it, for the reason the Codes tab dropped
                its own — filling the form in already writes the rule out, and
                restating it says the same words twice. */}
            <div className={s.builder}>
              {/* "When" and the closing clause in regular weight (owner,
                  2026-10-12 — D-350 amended: unbold "When" and "require the
                  following approvals:"). */}
              <Body1>When</Body1>
              {conditions.map((condition, index) => (
                <div key={index}>
                  {index > 0 && <Body1 className={s.connective}>and</Body1>}
                  <ConditionRow
                    condition={condition}
                    knownLabels={knownLabels}
                    canRemove={conditions.length > 1}
                    onChange={(next) => update(index, next)}
                    onRemove={() => remove(index)}
                  />
                </div>
              ))}
              <Button
                className={s.addRow}
                appearance="subtle"
                icon={<AddRegular />}
                disabled={conditions.length >= MAX_OVERSIGHT_CONDITIONS_PER_RULE}
                onClick={() =>
                  setConditions((previous) => [...previous, blankCondition(previous.length)])
                }
              >
                Add Condition
              </Button>
              {/* The closing clause, then the four approval steps drawn as the
                  Pipeline draws them (owner, 2026-10-09 — D-342), each clicked
                  on or off as its switch was. Lower case, because it continues
                  the "When ..." sentence above it (owner, 2026-10-09: "In the
                  Add/Edit Rule pop-up for Routing, "Require" should be lower
                  case."). */}
              <Body1 className={f.routeLead}>{ROUTING_RULE_CLOSING}</Body1>
              <div className={f.routeDiagram}>
                <ApprovalPathPicker
                  required={{
                    controller: approvals[REQUIRED_APPROVAL_FIELD.controller],
                    manager: approvals[REQUIRED_APPROVAL_FIELD.manager],
                    director: approvals[REQUIRED_APPROVAL_FIELD.director],
                    oversight: approvals[REQUIRED_APPROVAL_FIELD.oversight],
                  }}
                  onToggle={(key, on) => setApprovals((current) => toggleRequiredApproval(current, key, on))}
                />
              </div>
            </div>
          </DialogContent>
          {/* Status on EDIT ONLY — a new rule is Active, and a control that
              said so on the Add form would be asking a question with one
              sensible answer. A toggle beside Cancel and Save (D-201). */}
          {existing && (
            <DialogActions position="start">
              <StatusToggle value={isActive} onChange={setIsActive} />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// ---------------------------------------------------------------------------
// One condition: [scope] [field] [operator] [value]
// ---------------------------------------------------------------------------

function ConditionRow({
  condition,
  knownLabels,
  canRemove,
  onChange,
  onRemove,
}: {
  condition: OversightRuleConditionDraft
  knownLabels: Readonly<Record<string, string>>
  canRemove: boolean
  onChange: (next: OversightRuleConditionDraft) => void
  onRemove: () => void
}) {
  const s = useStyles()
  const fields = fieldsForScope(condition.scope)
  const operators = operatorsFor(condition.field, condition.scope)

  /**
   * Changing the SCOPE clears the field, the operator and the value; changing
   * the FIELD clears the operator and the value.
   *
   * Both are required rather than tidy: an operator carried over to a new type
   * cannot be saved, and leaving it on screen makes an unsaveable form look
   * complete.
   */
  function changeScope(scope: OversightRuleScope) {
    const first = fieldsForScope(scope)[0]
    if (first === undefined) return
    onChange({
      ...blankCondition(condition.position),
      scope,
      field: first,
      operator: operatorsFor(first, scope)[0] ?? 'is_any_of',
    })
  }

  function changeField(field: OversightRuleField) {
    onChange({
      ...blankCondition(condition.position),
      scope: condition.scope,
      field,
      operator: operatorsFor(field, condition.scope)[0] ?? 'is_any_of',
    })
  }

  return (
    <div className={s.conditionRow}>
      <Dropdown
        className={s.field}
        aria-label="Scope"
        selectedOptions={[condition.scope]}
        value={OVERSIGHT_SCOPE_CLAUSE[condition.scope]}
        onOptionSelect={(_, d) => changeScope(d.optionValue as OversightRuleScope)}
      >
        {OVERSIGHT_RULE_SCOPES.map((scope) => (
          <Option key={scope} value={scope} text={OVERSIGHT_SCOPE_CLAUSE[scope]}>
            {OVERSIGHT_SCOPE_CLAUSE[scope]}
          </Option>
        ))}
      </Dropdown>
      <Dropdown
        className={s.field}
        aria-label="Field"
        selectedOptions={[condition.field]}
        value={OVERSIGHT_FIELD_LABEL[condition.field]}
        onOptionSelect={(_, d) => changeField(d.optionValue as OversightRuleField)}
      >
        {fields.map((field) => (
          <Option key={field} value={field} text={OVERSIGHT_FIELD_LABEL[field]}>
            {OVERSIGHT_FIELD_LABEL[field]}
          </Option>
        ))}
      </Dropdown>
      <Dropdown
        className={s.field}
        aria-label="Operator"
        selectedOptions={[condition.operator]}
        value={OVERSIGHT_OPERATOR_LABEL[condition.operator]}
        onOptionSelect={(_, d) =>
          onChange({ ...condition, operator: d.optionValue as typeof condition.operator })
        }
      >
        {operators.map((operator) => (
          <Option key={operator} value={operator} text={OVERSIGHT_OPERATOR_LABEL[operator]}>
            {OVERSIGHT_OPERATOR_LABEL[operator]}
          </Option>
        ))}
      </Dropdown>
      <ConditionValue condition={condition} knownLabels={knownLabels} onChange={onChange} />
      <Tooltip content="Remove Condition" relationship="label">
        <Button
          appearance="subtle"
          icon={<DeleteRegular />}
          disabled={!canRemove}
          onClick={onRemove}
        />
      </Tooltip>
    </div>
  )
}

/** The value control, chosen by the field's type. */
function ConditionValue({
  condition,
  knownLabels,
  onChange,
}: {
  condition: OversightRuleConditionDraft
  knownLabels: Readonly<Record<string, string>>
  onChange: (next: OversightRuleConditionDraft) => void
}) {
  const s = useStyles()
  const type = OVERSIGHT_FIELD_TYPE[condition.field]

  // VENDORS ARE SEARCHED, everything else is held. There are 6,210 vendors and
  // one page holds 200, so `options` would silently omit most of the master.
  // Each carries the invoice modal's Vendor picker line under its name (owner,
  // 2026-10-01, D-260): the Vendor ID, the city and state, the remittance city
  // and state (`vendorOptionSubtext`).
  const searchVendorOptions = useCallback(
    async (term: string): Promise<MultiSelectOption[]> =>
      (await searchVendors(term)).map((v) => ({
        value: v.vendor_id,
        label: v.vendor_name,
        secondary: vendorOptionSubtext(v) || undefined,
      })),
    [],
  )

  const [options, setOptions] = useState<MultiSelectOption[] | null>(null)
  useEffect(() => {
    if (type !== 'entity' || condition.field === 'vendor') return
    let cancelled = false
    void (async () => {
      const loaded = await optionsForField(condition.field)
      if (!cancelled) setOptions(loaded)
    })()
    return () => {
      cancelled = true
    }
  }, [type, condition.field])

  if (type === 'entity') {
    const shared = {
      label: '',
      // "Value", as the textbox in the same slot is named (A-09).
      ariaLabel: 'Value',
      selected: condition.value_ids ?? [],
      onChange: (values: string[]) => onChange({ ...condition, value_ids: values }),
    }
    return condition.field === 'vendor' ? (
      <MultiSelectField
        {...shared}
        search={searchVendorOptions}
        knownLabels={knownLabels}
        placeholder="e.g. XEROX BUSINESS SOLUTIONS"
      />
    ) : (
      <MultiSelectField
        {...shared}
        options={options ?? []}
        placeholder={PLACEHOLDER_FOR_FIELD[condition.field] ?? 'e.g. SeniorLIFE Lehigh Valley'}
      />
    )
  }

  if (type === 'date') {
    return (
      <FieldInput
        className={s.field}
        type="date"
        aria-label="Value"
        value={condition.value_date ?? ''}
        onChange={(_, d) => onChange({ ...condition, value_date: d.value || null })}
      />
    )
  }

  if (type === 'money') {
    return (
      <MoneyValueInput
        value={condition.value_number}
        onChange={(next) => onChange({ ...condition, value_number: next })}
      />
    )
  }

  if (type === 'number') {
    return (
      <FieldInput
        className={s.field}
        aria-label="Value"
        placeholder="e.g. 2.5"
        value={condition.value_number ?? ''}
        onChange={(_, d) => onChange({ ...condition, value_number: filterMoneyInput(d.value) || null })}
      />
    )
  }

  return (
    <FieldInput
      className={s.field}
      aria-label="Value"
      placeholder={condition.field === 'line_description' ? 'e.g. produce' : 'e.g. INV-4471'}
      value={condition.value_text ?? ''}
      onChange={(_, d) => onChange({ ...condition, value_text: d.value || null })}
    />
  )
}

/**
 * A money value, shown AS MONEY (owner, 2026-09-21) — the same figure the
 * finished sentence prints, so the editor and the row agree.
 *
 * It formats on blur rather than on every keystroke: reformatting mid-typing
 * moves the caret and fights the person entering the number. While focused it
 * holds exactly what was typed, filtered to what a money field may contain
 * (`filterMoneyInput`, shared with the invoice editor). The STORED value is
 * always the plain decimal string the wire takes — the grouping and the dollar
 * sign are display only.
 */
function MoneyValueInput({
  value,
  onChange,
}: {
  value: string | null
  onChange: (next: string | null) => void
}) {
  const s = useStyles()
  const [typing, setTyping] = useState<string | null>(null)
  const shown = typing ?? (value === null || value.trim() === '' ? '' : formatCurrency(value))
  return (
    <FieldInput
      className={s.field}
      aria-label="Value"
      placeholder="e.g. $1,000.00"
      value={shown}
      onFocus={() => setTyping(value ?? '')}
      onBlur={() => setTyping(null)}
      onChange={(_, d) => {
        const filtered = filterMoneyInput(d.value)
        setTyping(filtered)
        onChange(filtered === '' ? null : filtered)
      }}
    />
  )
}

/** Every placeholder is an EXAMPLE, never a description of the control.
 *
 *  Each is a NAME that exists: a name is what each option shows first. The
 *  Account, Department and Code lists also find an entry by its ID, which each
 *  option shows under its name (owner, 2026-09-25 — D-182), so typing `66-549`
 *  finds Food & Staples. The Code example was an ID, `502719-67-506`, which is not
 *  in the chart and which the picker cannot find by typing it (QA 2026-09-23,
 *  T1-07). The three line examples are now one code and its two halves:
 *  `689309-66-549` is DIET FOOD & STAPLES, Dietary with Food & Staples. */
const PLACEHOLDER_FOR_FIELD: Partial<Record<OversightRuleField, string>> = {
  source: 'e.g. Fleet Maintenance',
  company: 'e.g. SeniorLIFE Lehigh Valley',
  line_account: 'e.g. Food & Staples',
  line_department: 'e.g. Dietary',
  line_code: 'e.g. DIET FOOD & STAPLES',
}

/** The whole list, for the four masters small enough to hold in memory. The
    Account, Department and Code options carry their ID as `secondary`, so a
    typed ID finds them while the name is what reads first (D-182). */
async function optionsForField(field: OversightRuleField): Promise<MultiSelectOption[]> {
  switch (field) {
    // The systems an invoice can come from: a fixed list, nothing to fetch (D-382).
    case 'source':
      return sourceSystemOptions()
    case 'company': {
      const companies = await loadCompaniesForManagement()
      return companies.map((c) => ({ value: c.company_id, label: c.company_name }))
    }
    case 'line_account': {
      const accounts = await loadAccounts()
      return accounts.map((a) => ({ value: a.account_id, label: a.account_name, secondary: a.account_id, searchTerms: a.search_terms }))
    }
    case 'line_department': {
      const departments = await loadDepartments()
      return departments.map((d) => ({ value: d.department_id, label: d.department_name, secondary: d.department_id, searchTerms: d.search_terms }))
    }
    case 'line_code': {
      const codes = await loadAllCodes()
      return codes.map((c) => ({ value: c.code_id, label: c.code_name, secondary: c.code_id }))
    }
    default:
      return []
  }
}
